import React, {useCallback, useMemo, useState} from 'react';

import clsx from 'clsx';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {TemplateType} from 'src/openapi/admin/models/TemplateType';
import {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Loading from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';


import {useRenameTemplate} from './hooks';

import {
    editSearchColumnsTemplateRow,
    editSearchColumnsTemplateRowActive,
} from './edit-search-columns.module.styl';


type Props = {
    busy: boolean,
    canCopy: boolean,
    template: UserSearchTemplate,
    templates: UserSearchTemplate[],
    setActiveTemplate: (template: UserSearchTemplate) => void,
    hoveredTemplateId: number | null,
    setHoveredTemplateId: (templateId: number | null) => void,
    onRemoveTemplate: (template: UserSearchTemplate) => void,
    onRenameDone: () => void,
    onRenameFail: (error: string) => void,
    onRenameCancel: () => void,
    copyTemplate: (template: UserSearchTemplate) => void,
};

export const TemplateRow = (props: Props) => {
    const {
        busy,
        canCopy,
        template,
        templates,
        hoveredTemplateId,
        setActiveTemplate,
        setHoveredTemplateId,
        onRemoveTemplate,
        onRenameDone,
        onRenameFail,
        onRenameCancel,
        copyTemplate,
    } = props;

    const [mode, setMode] = useState<'default' | 'rename'>('default');
    const [renameError, setRenameError] = useState<string | undefined>(undefined);
    const [layoutName, setLayoutName] = useState(template.templateName);
    const {busy: renameBusy, event: renameLayout} = useRenameTemplate({
        templateId: template.templateId,
        onSuccess: () => {
            setMode('default');
            onRenameDone();
        },
        onFail: onRenameFail,
    });

    useEffectWithDeps((prev, current) => {
        if (prev !== current) {
            setLayoutName(template.templateName);
        }
    }, template.templateName);

    const cancelRenaming = useCallback(() => {
        setMode('default');
        onRenameCancel();
        setLayoutName(template.templateName);
    }, [onRenameCancel, template]);

    const changeName = useCallback<React.ChangeEventHandler<HTMLInputElement>>(event => {
        const name = event.target.value;
        setLayoutName(name);
        setRenameError(
            templates.some(({templateName: other, templateId, searchType}) => (
                other.toLocaleLowerCase().trim() === name.toLocaleLowerCase().trim() &&
                searchType === template?.searchType &&
                templateId !== template?.templateId
            )) ? 'Layout with this name already exists' : undefined,
        );
    }, [templates, template]);

    const confirmRename = useCallback(() => {
        if (renameError === 'Layout with this name already exists' || !layoutName.trim()) {
            return;
        }

        if (template.templateName.trim() !== layoutName.trim()) {
            renameLayout(layoutName);
        } else {
            cancelRenaming();
        }
    }, [layoutName, renameLayout, template, renameError, cancelRenaming]);

    const setRenameMode = useCallback(() => {
        setMode('rename');
    }, []);

    const selectTemplate = useCallback(() => {
        setActiveTemplate(template);
    }, [template, setActiveTemplate]);

    const removeTemplate = useCallback(() => {
        onRemoveTemplate(template);
    }, [template, onRemoveTemplate]);

    const classes = clsx(editSearchColumnsTemplateRow, [
        (template.isActive) && editSearchColumnsTemplateRowActive,
    ]);

    const endAdornment = useMemo(() => {
        if (renameBusy) {
            return <Loading type="ICON" fast={true}/>;
        }

        const isNameChanged = template.templateName.trim() !== layoutName.trim();

        return (
            <Group alignItems="center" gap="s">
                <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                    <Icon
                        ref={anchorRef}
                        {...getAnchorProps()}
                        type={Icons.CLOSE}
                        size="Size_14"
                        onClick={cancelRenaming}
                    />
                )}>
                    Cancel
                </Tooltip>
                {renameError ? (
                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                        <Icon
                            className="error-color"
                            ref={anchorRef}
                            {...getAnchorProps()}
                            type={Icons.ERROR}
                        />
                    )}>
                        {renameError}
                    </Tooltip>
                ) : null}
                {(!renameError && layoutName && isNameChanged) ? (
                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                        <Icon
                            {...getAnchorProps()}
                            ref={anchorRef}
                            type={Icons.CHECK}
                            size="Size_14"
                            onClick={confirmRename}
                        />
                    )}>
                        Submit
                    </Tooltip>
                ) : null}
            </Group>
        );
    }, [renameBusy, renameError, confirmRename, layoutName, template.templateName, cancelRenaming]);

    if (mode === 'rename') {
        return (
            <Input
                autoFocus={true}
                value={layoutName}
                color={renameError ? 'ERROR' : undefined}
                endAdornment={endAdornment}
                onChange={changeName}
                maxLength={64}
                disabled={renameBusy}
                placeholder="Enter layout name"
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        event.currentTarget.blur();
                        confirmRename();
                    }
                }}
            />
        );
    }

    return (
        <div
            className={classes}
            key={template.templateId}
            onMouseEnter={() => setHoveredTemplateId(template.templateId)}
            onMouseLeave={() => setHoveredTemplateId(null)}>
            <div onClick={(!template.isActive && !busy) ? selectTemplate : undefined}>
                <Group alignItems="center">
                    {template.isActive ? (
                        <Icon type={Icons.CHECK}/>
                    ) : null}
                    {busy ? <Loading type="ICON" fast={true}/> : null}
                    <div style={{width: 150}}>
                        <EllipsisTextHint title={layoutName}>
                            {layoutName}
                        </EllipsisTextHint>
                    </div>
                    {(template.templateType === TemplateType.DEFAULT && hoveredTemplateId !== template.templateId)
                        ? <span>(default)</span>
                        : null}
                </Group>
            </div>
            {(hoveredTemplateId === template.templateId && template.templateType !== TemplateType.DEFAULT) ? (
                <Group alignItems="center">
                    <Tooltip
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Icon
                                className={canCopy ? '' : 'disabled'}
                                ref={anchorRef}
                                {...getAnchorProps()}
                                type={Icons.COPY}
                                size="Size_14"
                                onClick={canCopy ? () => copyTemplate(template) : undefined}
                            />
                        )}>
                        {canCopy ? 'Copy' : 'You cannot have more than 10 layouts'}
                    </Tooltip>
                    <Tooltip
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Icon
                                ref={anchorRef}
                                {...getAnchorProps()}
                                type={Icons.PENCIL}
                                size="Size_14"
                                onClick={setRenameMode}
                            />
                        )}>
                        Rename
                    </Tooltip>
                    <Tooltip
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Icon
                                ref={anchorRef}
                                {...getAnchorProps()}
                                type={Icons.TRASH}
                                size="Size_14"
                                onClick={removeTemplate}
                            />
                        )}>
                        Remove
                    </Tooltip>
                </Group>
            ) : null}

            {(hoveredTemplateId === template.templateId && template.templateType === TemplateType.DEFAULT) ? (
                <Group alignItems="center">
                    <Tooltip
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Icon
                                className={canCopy ? '' : 'disabled'}
                                ref={anchorRef}
                                {...getAnchorProps()}
                                type={Icons.COPY}
                                size="Size_14"
                                onClick={canCopy ? () => copyTemplate(template) : undefined}
                            />
                        )}>
                        {canCopy ? 'Copy' : 'You cannot have more than 10 layouts'}
                    </Tooltip>
                </Group>
            ) : null}
        </div>
    );
};
